import type { ScoringEventType } from '../../sdk/scoring';
import type { GameplayTunables } from '../../shared/types/tunables';

/**
 * The wrong-answer ladder (MVP notes §13) and the shard tiers it decides (§14, D-011).
 *
 * Pure functions, deliberately: the runtime owns the clock, the content and the narration, and
 * these are the rules a reviewer should be able to read in one sitting. Two of them are product
 * invariants rather than mechanics —
 *
 *   - nothing here can subtract shards. There is no negative `ScoringEventType` and every branch
 *     returns a positive award, so "no score is removed" is unrepresentable rather than merely
 *     unimplemented (D-021).
 *   - `nextStepAfterTimeout` never returns `RETRY`, and the runtime never increments the attempt
 *     number for it. Time expiring escalates the *help* a child is offered; it is not an answer,
 *     so it cannot be a wrong one (MVP notes §12).
 */

export type LadderStep =
  /** Funny gentle reaction, same question again. */
  | 'RETRY'
  /** Short teaching moment, then the same question again. */
  | 'TEACHING'
  /** The easier variant of this curated slot (D-034 PS-1). */
  | 'SIMPLIFIED'
  /** Nothing left to offer; the mission continues rather than looping. */
  | 'MOVE_ON';

export interface LadderContext {
  /** 1 for the first try. Only a child's answer advances it. */
  attemptNumber: number;
  /** A teaching moment (full or hint) has already run for this round. */
  teachingUsed: boolean;
  /** The stage allows teaching, the item names one, and the player's cap is unspent. */
  teachingAvailable: boolean;
  /** A simpler variant of this curated slot exists. */
  simplifiedAvailable: boolean;
  /** The child is already answering the simplified variant. */
  onSimplified: boolean;
}

/** What to offer next once a child has answered incorrectly. */
export function nextStepAfterMiss(context: LadderContext): LadderStep {
  if (context.attemptNumber <= 1) return 'RETRY';
  return escalate(context);
}

/**
 * What to offer when the round timer's last step is reached with no answer.
 *
 * Identical to a miss *except* that it can never be `RETRY`: a retry is what follows a wrong
 * answer, and no answer was given.
 */
export function nextStepAfterTimeout(context: LadderContext): LadderStep {
  return escalate(context);
}

function escalate(context: LadderContext): LadderStep {
  if (!context.teachingUsed && context.teachingAvailable) return 'TEACHING';
  if (!context.onSimplified && context.simplifiedAvailable) return 'SIMPLIFIED';
  return 'MOVE_ON';
}

/**
 * Which scoring tier a correct answer earns.
 *
 * There is no fourth tier below the simplified version (D-011), and a round that reached the
 * third interaction without teaching — because the player's teaching cap was already spent —
 * scores the same tier as one that had it. The tier describes how much help the answer needed,
 * not which help happened to be available.
 */
export function scoringEventTypeFor(context: LadderContext): ScoringEventType {
  if (context.onSimplified) return 'SIMPLIFIED_AFTER_THIRD_INTERACTION';
  if (context.teachingUsed || context.attemptNumber >= 3) return 'CORRECT_AFTER_TEACHING';
  if (context.attemptNumber === 2) return 'CORRECT_AFTER_RETRY';
  return 'CORRECT_FIRST_ATTEMPT';
}

/** The award for a correct answer at this point on the ladder. Never negative, never zero. */
export function shardsForOutcome(context: LadderContext, tunables: GameplayTunables): number {
  switch (scoringEventTypeFor(context)) {
    case 'CORRECT_FIRST_ATTEMPT':
      return tunables.shards.first;
    case 'CORRECT_AFTER_RETRY':
      return tunables.shards.retry;
    case 'SIMPLIFIED_AFTER_THIRD_INTERACTION':
      return tunables.shards.thirdInteraction;
    default:
      return tunables.shards.afterTeaching;
  }
}
